import React, { forwardRef, useMemo, useCallback, useState } from "react";
import Select from "react-select";
import arePropsEqual from "../../../utils/arePropsEqual";

const CFSelect = forwardRef((props, ref) => {
  const {
    formInputName,
    defaultValue,
    onChange,
    styles,
    menuPortalZIndex,
    ...selectProps
  } = props;

  const mergedStyles = useMemo(() => {
    const userStyles = styles || {};

    return {
      ...userStyles,
      // react-select defaults the menu to width: 100% of the control (and
      // portals pin that width on the portal wrapper). Absolute-positioned
      // menus do not contribute to the portal's intrinsic size, so growing
      // the portal with max-content has no effect — override the menu itself
      // so long option labels can expand past the control width.
      menu: (base, state) => {
        const userMenuStyles = userStyles.menu
          ? userStyles.menu(base, state)
          : {};

        return {
          ...base,
          ...userMenuStyles,
          width: "max-content",
          minWidth: "100%",
        };
      },
      menuList: (base, state) => {
        const userMenuListStyles = userStyles.menuList
          ? userStyles.menuList(base, state)
          : {};

        return {
          ...base,
          ...userMenuListStyles,
          // overflow-y: auto forces overflow-x to auto as well; keep horizontal
          // content from creating a sideways scroll inside the menu.
          overflowX: "hidden",
        };
      },
      menuPortal: (base, state) => {
        const userPortalStyles = userStyles.menuPortal
          ? userStyles.menuPortal(base, state)
          : {};

        return {
          ...base,
          ...userPortalStyles,
          zIndex: userPortalStyles.zIndex ?? menuPortalZIndex ?? 2000,
        };
      },
    };
  }, [styles, menuPortalZIndex]);

  const [internalValue, setInternalValue] = useState(
    formInputName ? defaultValue ?? props.value ?? [] : undefined
  );

  const isFormSync = Boolean(formInputName);
  const value = isFormSync ? internalValue : props.value;

  const handleChange = useCallback(
    (selected, actionMeta) => {
      if (isFormSync) setInternalValue(selected);
      onChange?.(selected, actionMeta);
    },
    [isFormSync, onChange]
  );

  const isFontDropdown =
    props.font_dropdown ||
    props?.setting_name?.indexOf("font") > -1 ||
    props?.name?.indexOf("font") > -1;

  const isLargeFontControl =
    isFontDropdown && props.font_dropdown_large !== false;

  const options = useMemo(() => {
    if (props.options[0] && props.options[0].text) {
      return props.options
        .filter((e) => e.condition !== false)
        .map((option) => {
          return { value: option.value, label: option.text };
        });
    }
    return props.options;
  }, [props.options]);

  const formatOptionLabel = useCallback(
    ({ value, label, billableActionHref, tooltip }, { context }) => {
      const isFontDropdown =
        props.font_dropdown ||
        props?.setting_name?.indexOf("font") > -1 ||
        props?.name?.indexOf("font") > -1;

      let icon;
      props.options.forEach((option) => {
        if (option.options) {
          const selectedOption = option.options.filter(
            (option) => option.value == value
          )[0];

          if (selectedOption && selectedOption.icon) {
            icon = selectedOption.icon;
          }
        }
      });

      const fontOptionClass = isFontDropdown
        ? isLargeFontControl
          ? "!text-[20px] !leading-[30px]"
          : "!text-[15px] !leading-[22px]"
        : "";

      const shouldShowBillableActionLink =
        context === "menu" && Boolean(billableActionHref);

      const wrapperClass = shouldShowBillableActionLink
        ? `${fontOptionClass} flex items-center justify-between gap-3 whitespace-nowrap`
        : fontOptionClass;

      return (
        <div
          data-value={value}
          title={tooltip || undefined}
          className={wrapperClass}
          style={{
            fontFamily: isFontDropdown ? `${value}, Inter, sans-serif` : null,
          }}
          onMouseOver={() => {
            if (
              isFontDropdown &&
              value &&
              window.ctas?.builder?.fonts?.load_font_family
            ) {
              window.ctas.builder.fonts.load_font_family(value);
            }
          }}
        >
          {shouldShowBillableActionLink ? (
            <>
              <div className="flex items-center whitespace-nowrap">
                {icon && <img className="w-[13px] h-auto mr-5" src={icon} />}
                <span className="whitespace-nowrap">{label}</span>
              </div>
              <a
                href={billableActionHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View billable actions usage"
                title="Billable action"
                className="ml-2 inline-flex items-center justify-center min-w-[18px] w-[18px] h-[18px] rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors no-underline"
                onMouseDown={(event) => {
                  event.stopPropagation();
                  if (event.button !== 0) return;
                  event.preventDefault();
                  window.open(
                    billableActionHref,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M13.2 2.5L5 13h5.2L8.8 21.5 19 9h-5.2L13.2 2.5z"
                    fill="#FFFFFF"
                  />
                </svg>
              </a>
            </>
          ) : (
            <>
              {icon && <img className="w-[13px] h-auto mr-5" src={icon} />}
              {label}
            </>
          )}
        </div>
      );
    },
    [
      props.options,
      props.font_dropdown,
      props.setting_name,
      props.name,
      isLargeFontControl,
    ]
  );

  const classNames = useMemo(
    () => ({
      control: () =>
        `w-full bg-white dark:bg-slate-950 text-black dark:text-white !rounded-xl border-solid border-[1px] !border-slate-200 dark:!border-slate-700 focus:!border-slate-200 !outline-none !shadow-none ${
          isLargeFontControl
            ? "!min-h-[56px] px-4 text-2xl"
            : `!min-h-[34px] px-3 ${
                isFontDropdown && !isLargeFontControl
                  ? "text-[15px]"
                  : "text-xl"
              }`
        }`,
      dropdownIndicator: () => "!p-[0px]",
      indicatorSeparator: () => "!w-[0px]",
      input: () =>
        "text-black dark:text-white outline-none focus:outline-none focus:ring-0 appearance-none shadow-none",
      menu: () =>
        `min-w-full w-max bg-white dark:bg-slate-950 ${
          isFontDropdown && !isLargeFontControl ? "text-[15px]" : "text-xl"
        } text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700`,
      option: ({ isFocused, isSelected }) =>
        `!cursor-pointer !text-black dark:!text-white whitespace-nowrap ${
          isSelected
            ? "!bg-blue-600 !text-white"
            : isFocused
            ? "!bg-slate-100 dark:!bg-slate-800"
            : ""
        } hover:!text-black active:!text-black focus:!text-black hover:!bg-slate-100 active:!bg-slate-100 focus:!bg-slate-800 dark:hover:!text-white dark:active:!text-white dark:focus:!text-white dark:hover:!bg-slate-800 dark:active:!bg-slate-800 dark:focus:!bg-slate-800`,
      placeholder: () => "!text-slate-400 dark:!text-slate-500",
      singleValue: () =>
        `text-black dark:text-white truncate ${
          isLargeFontControl
            ? "!text-[20px] !line-height-[30px]"
            : isFontDropdown
            ? "!text-[15px] !line-height-[22px]"
            : ""
        } ${props.singleValueClassName || ""}`,
      multiValue: () =>
        "select-multi-value !bg-white dark:!bg-slate-950 border-solid border-[1px] !border-slate-200 dark:!border-slate-700",
      valueContainer: () => (isLargeFontControl ? "!py-3 !px-0" : "!p-[0px]"),
    }),
    [props.singleValueClassName, isFontDropdown, isLargeFontControl]
  );

  const mergedClassNames = useMemo(() => {
    const callerClassNames = props.classNames || {};
    const merged = { ...callerClassNames };
    Object.keys(classNames).forEach((slot) => {
      merged[slot] = (state) =>
        `${classNames[slot](state)} ${callerClassNames[slot]?.(state) || ""}`;
    });
    return merged;
  }, [classNames, props.classNames]);

  const selectedTitle = (() => {
    if (value == null || value === "") return "";
    if (Array.isArray(value)) {
      return value
        .map((option) => option?.tooltip || option?.label || option?.value)
        .filter(Boolean)
        .join(", ");
    }
    if (typeof value === "object") {
      return value.tooltip || value.label || value.value || "";
    }
    return String(value);
  })();

  return (
    <React.Fragment>
      {isFormSync && (
        <input
          type="hidden"
          name={formInputName}
          value={
            Array.isArray(value)
              ? JSON.stringify(
                  value.map((o) => (typeof o === "object" ? o.value : o))
                )
              : ""
          }
        />
      )}
      <div className="w-full min-w-0" title={selectedTitle || undefined}>
        <Select
          ref={ref}
          className={`w-full ${props.className}`}
          {...selectProps}
          styles={mergedStyles}
          options={options}
          classNames={mergedClassNames}
          formatOptionLabel={formatOptionLabel}
          value={value}
          onChange={isFormSync ? handleChange : onChange}
          defaultValue={isFormSync ? undefined : defaultValue}
        />
      </div>

      <style dangerouslySetInnerHTML={{ __html: style.stylesheet() }} />
    </React.Fragment>
  );
});

const style = {
  stylesheet: () => {
    // prettier-ignore
    const desktopStyles = ``;

    return desktopStyles;
  },
};

export default React.memo(CFSelect, arePropsEqual);
